import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { LegendKey } from "../models/chart_model";

/**
 * useLegendFilter
 *
 * Manages legend selection state with smart pattern matching:
 * - Maintains selection when adding new legends
 * - Auto-selects new legends that match the pattern of previously selected legends
 * - Only resets to "show all" when NO previously selected legends exist
 */
export function useLegendFilter(
  allLegends: LegendKey[],
  resetDeps: any[] = [],
) {
  const [selectedLegends, setSelectedLegends] = useState<LegendKey[]>([]);
  const prevLegendsSignature = useRef<string>("");
  const prevSelectedLegends = useRef<LegendKey[]>([]);

  const legendsSignature = useMemo(() => {
    return allLegends
      .map((leg) => leg.dataKey)
      .sort()
      .join("|");
  }, [allLegends]);

  // Extract pattern from legend name (everything after the date/month part)
  const extractPattern = useCallback((legend: LegendKey): string => {
    // Use the name field for pattern matching as it's more reliable
    let pattern = legend.name || legend.dataKey;

    // Remove month patterns like "JAN-25", "FEB-26", "DEC-24", etc.
    pattern = pattern.replace(/\b[A-Z]{3}-\d{2}\b\s*/g, "");

    // Remove date ranges like "2025-12-10 to 2025-12-10"
    pattern = pattern.replace(
      /\d{4}-\d{2}-\d{2}\s+to\s+\d{4}-\d{2}-\d{2}\s*/g,
      "",
    );

    // Remove single dates like "2025-12-10"
    pattern = pattern.replace(/\d{4}-\d{2}-\d{2}\s*/g, "");

    // Trim and return the pattern (e.g., "AVVNL" or "(Forecast D+1) RVPN Drawal")
    return pattern.trim();
  }, []);

  // Smart reset logic: maintain and extend matching selections
  useEffect(() => {
    const hasStructureChanged =
      prevLegendsSignature.current !== legendsSignature;

    if (hasStructureChanged && prevLegendsSignature.current !== "") {
      // Use the previous selected legends from ref, not current state
      if (prevSelectedLegends.current.length > 0) {
        // Extract patterns from previously selected legends
        const selectedPatterns = new Set(
          prevSelectedLegends.current.map((leg) => extractPattern(leg)),
        );

        // Find all legends (old and new) that match these patterns
        const matchingLegends = allLegends.filter((leg) => {
          const pattern = extractPattern(leg);
          return selectedPatterns.has(pattern);
        });

        if (matchingLegends.length > 0) {
          // Found matching patterns - select all matching legends
          setSelectedLegends(matchingLegends);
          prevSelectedLegends.current = matchingLegends;
        } else {
          // No matches found - reset to show all
          setSelectedLegends([]);
          prevSelectedLegends.current = [];
        }
      }

      prevLegendsSignature.current = legendsSignature;
    } else if (prevLegendsSignature.current === "") {
      // First load
      prevLegendsSignature.current = legendsSignature;
    }
  }, [legendsSignature, allLegends, extractPattern]); // Removed selectedLegends from deps

  // Update ref when user manually selects legends
  useEffect(() => {
    prevSelectedLegends.current = selectedLegends;
  }, [selectedLegends]);

  // Optional reset on additional dependencies
  useEffect(() => {
    if (resetDeps.length > 0) {
      const hasStructureChanged =
        prevLegendsSignature.current !== legendsSignature;
      if (hasStructureChanged) {
        // Let the main effect handle this
      }
    }
  }, [...resetDeps, legendsSignature]);

  const handleLegendSelect = useCallback((legends: LegendKey[]) => {
    setSelectedLegends(legends);
  }, []);

  const visibleLegends = useMemo(() => {
    if (selectedLegends.length === 0) {
      return allLegends;
    }

    if (selectedLegends.length === allLegends.length) {
      return allLegends;
    }

    // Show only selected legends that still exist in allLegends
    const visible = allLegends.filter((leg) =>
      selectedLegends.some((sel) => sel.dataKey === leg.dataKey),
    );

    return visible;
  }, [selectedLegends, allLegends]);

  return {
    visibleLegends,
    handleLegendSelect,
    selectedLegends,
  };
}

/**
 * Alternative version with explicit reset control and pattern matching
 */
export function useLegendFilterWithReset(
  allLegends: LegendKey[],
  forceReset: boolean = false,
) {
  const [selectedLegends, setSelectedLegends] = useState<LegendKey[]>([]);
  const prevLegendsSignature = useRef<string>("");
  const prevSelectedLegends = useRef<LegendKey[]>([]);

  const legendsSignature = useMemo(() => {
    return allLegends
      .map((leg) => leg.dataKey)
      .sort()
      .join("|");
  }, [allLegends]);

  const extractPattern = useCallback((legend: LegendKey): string => {
    let pattern = legend.name || legend.dataKey;

    // Remove month patterns like "JAN-25", "FEB-26", etc.
    pattern = pattern.replace(/\b[A-Z]{3}-\d{2}\b\s*/g, "");

    // Remove date ranges like "2025-12-10 to 2025-12-10"
    pattern = pattern.replace(
      /\d{4}-\d{2}-\d{2}\s+to\s+\d{4}-\d{2}-\d{2}\s*/g,
      "",
    );

    // Remove single dates like "2025-12-10"
    pattern = pattern.replace(/\d{4}-\d{2}-\d{2}\s*/g, "");

    return pattern.trim();
  }, []);

  // Auto-reset when structure changes with pattern matching
  useEffect(() => {
    const hasStructureChanged =
      prevLegendsSignature.current !== legendsSignature;

    if (hasStructureChanged && prevLegendsSignature.current !== "") {
      if (prevSelectedLegends.current.length > 0) {
        const selectedPatterns = new Set(
          prevSelectedLegends.current.map((leg) => extractPattern(leg)),
        );

        const matchingLegends = allLegends.filter((leg) => {
          const pattern = extractPattern(leg);
          return selectedPatterns.has(pattern);
        });

        if (matchingLegends.length > 0) {
          setSelectedLegends(matchingLegends);
          prevSelectedLegends.current = matchingLegends;
        } else {
          setSelectedLegends([]);
          prevSelectedLegends.current = [];
        }
      }

      prevLegendsSignature.current = legendsSignature;
    } else if (prevLegendsSignature.current === "") {
      prevLegendsSignature.current = legendsSignature;
    }
  }, [legendsSignature, allLegends, extractPattern]); // Removed selectedLegends from deps

  // Update ref when user manually selects legends
  useEffect(() => {
    prevSelectedLegends.current = selectedLegends;
  }, [selectedLegends]);

  // Force reset when explicitly requested
  useEffect(() => {
    if (forceReset) {
      setSelectedLegends([]);
      prevSelectedLegends.current = [];
    }
  }, [forceReset]);

  const handleLegendSelect = useCallback((legends: LegendKey[]) => {
    setSelectedLegends(legends);
  }, []);

  const visibleLegends = useMemo(() => {
    if (selectedLegends.length === 0) {
      return allLegends;
    }

    if (selectedLegends.length === allLegends.length) {
      return allLegends;
    }

    return allLegends.filter((leg) =>
      selectedLegends.some((sel) => sel.dataKey === leg.dataKey),
    );
  }, [selectedLegends, allLegends]);

  return {
    visibleLegends,
    handleLegendSelect,
    selectedLegends,
    resetSelection: () => setSelectedLegends([]),
  };
}
